/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable jsx-a11y/anchor-is-valid */

import React, { useContext, useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import _, { constant, compose, mapObject } from 'underscore';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { Link, FontIcon } from '@bingads-webui-react/fabric-control';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { TargetDetailList } from './components/detail-list';
import { getStyles } from './style';
import { validateFn } from './components/generate-column';
import { ErrorString } from './components/shared';
import { isValueOverlap, setIsDirtyByNotifyFn, overlapErrorMsg } from './components/utility';

export const usePageControl = () => {
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  return {
    pageNumber, setPageNumber, pageSize, setPageSize,
  };
};

export const GridTargetsBaseApp = (props) => {
  const i18n = useContext(I18nContext);
  const {
    data,
    setData,
    basicConfig,
    handleAddLine,
    getFieldErrorMsg,
    classes,
    validateParams,
    apiErrors,
    patchTargetsApiErrMsg,
  } = props;

  const [isDirty, setIsDirty] = useState({});
  const [errMsg, setErrMsg] = useState('');
  const [keySuffix, setKeySuffix] = useState('');

  const {
    pageNumber, setPageNumber, pageSize, setPageSize,
  } = usePageControl();

  const {
    enablePagination,
  } = basicConfig;

  const shift = enablePagination ? (pageNumber - 1) * pageSize : 0;

  const debounceFn = useCallback(_.debounce((callback) => { callback(); }, 500), []);
  const handleChangeInput = useCallback((...rest) => {
    const [{ fieldName, index }, value] = rest;
    const realIndex = index + shift;
    const currentLevelSettings = [...data.currentLevelSettings];
    currentLevelSettings[realIndex] = { ...currentLevelSettings[realIndex], [fieldName]: value };
    props.setData({ ...data, currentLevelSettings });
  }, [data, props, shift]);

  const handleBatchChangeInput = useCallback((...rest) => {
    const [{ fieldName, index }] = rest;
    const realIndex = index + shift;
    const currentLevelSettings = [...data.currentLevelSettings];
    currentLevelSettings[realIndex] = { ...currentLevelSettings[realIndex], ...fieldName };
    props.setData({ ...data, currentLevelSettings });
  }, [data, props, shift]);


  const setIsDirtyByNotify = useCallback(() => {
    setIsDirtyByNotifyFn({ data, setKeySuffix, setIsDirty });
  }, [data.reset, data.isInvalid, data.remove]);
  setIsDirtyByNotify();

  // When the valid value range or input value changes, check again, show or hide error message on the top right corner of jumbo card.
  useEffect(() => { setErrMsg(isValueOverlap(validateParams.targetName, data.currentLevelSettings)); debounceFn(() => validateFn(validateParams)); }, [data.currentLevelSettings, data.useParentLevel, basicConfig.minBid, basicConfig.maxBid, basicConfig.extraBid, basicConfig.onlyExtra]);
  useEffect(() => { setKeySuffix(new Date().getTime()); }, [data.useParentLevel]);
  useEffect(() => {
    if ((!_.isEmpty(isDirty) || data.useParentLevel) && apiErrors && apiErrors.length) {
      patchTargetsApiErrMsg(validateParams.targetName, []);
    }
  }, [isDirty, data.useParentLevel]);
  const localizeErrorMsg = useCallback(
    error => (overlapErrorMsg[error] ? i18n.getString(overlapErrorMsg[error]) : error),
    [i18n]
  );

  return (
    <div className={classnames(classes.criterionSettingContainer, 'criterion-setting-container')}>
      <div className={classnames(basicConfig.extraClass ? basicConfig.extraClass : '', 'table-container')}>
        <TargetDetailList
          columns={basicConfig.columns}
          minBid={basicConfig.minBid}
          maxBid={basicConfig.maxBid}
          extraBid={basicConfig.extraBid}
          onlyExtra={basicConfig.onlyExtra}
          data={data}
          setData={setData}
          isDirty={isDirty}
          setIsDirty={setIsDirty}
          showAddBtn={basicConfig.showAddBtn}
          noBidAdjustment={basicConfig.noBidAdjustment}
          handleChangeInput={handleChangeInput}
          handleBatchChangeInput={handleBatchChangeInput}
          getFieldErrorMsg={getFieldErrorMsg}
          enablePagination={basicConfig.enablePagination}
          disableDecrease={basicConfig.disableDecrease}
          pageNumber={pageNumber}
          setPageNumber={setPageNumber}
          pageSize={pageSize}
          setPageSize={setPageSize}
          hideWhenEmpty={basicConfig.hideWhenEmpty}
          validateParams={validateParams}
          keySuffix={keySuffix}
          patchTargetsApiErrMsg={patchTargetsApiErrMsg}
        />
      </div>
      {!data.useParentLevel && errMsg && <ErrorString error={localizeErrorMsg(errMsg)} />}
      {
        basicConfig.showAddBtn && (
          <div>
            <Link className="target-add-btn" disabled={data.useParentLevel || (basicConfig.maxLimit && data.currentLevelSettings.filter(item => item.Status !== 'Deleted').length >= basicConfig.maxLimit)} onClick={handleAddLine}>
              <FontIcon aria-label="Add" iconName="Add" /> {i18n.getString(_TL_('Add another'))}
            </Link>
          </div>
        )
      }
    </div>
  );
};


GridTargetsBaseApp.propTypes = {
  data: PropTypes.shape({}).isRequired,
  setData: PropTypes.func,
  basicConfig: PropTypes.shape({}).isRequired,
  handleAddLine: PropTypes.func,
  getFieldErrorMsg: PropTypes.func,
  classes: PropTypes.shape(mapObject(
    getStyles({}, { palette: {} }),
    constant(PropTypes.string.isRequired)
  )).isRequired,
  validateParams: PropTypes.shape({}),
  patchTargetsApiErrMsg: PropTypes.func,
  apiErrors: PropTypes.arrayOf(PropTypes.string),
};

GridTargetsBaseApp.defaultProps = {
  setData() {},
  handleAddLine() { },
  getFieldErrorMsg() { },
  validateParams: {},
  patchTargetsApiErrMsg() {},
  apiErrors: [],
};

export const GridTargetsBase = compose(WrappedComponent => withDefaultStyles(WrappedComponent, getStyles))(GridTargetsBaseApp);
